import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '@/components/pos/intl-locale';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatUZS } from '@/lib/format';
import POSLayout from '@/components/layout/POSLayout';
import { Button } from '@/components/ui/button';
import { CheckCircle, Printer, ArrowLeft } from 'lucide-react';
import { useMerchant } from '@/hooks/api/merchant';

export default function Receipt() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const receiptRef = useRef<HTMLDivElement>(null);
  const { data: merchant } = useMerchant();
  const state = location.state as {
    total: number;
    method: string;
    items: number;
    saleId?: number;
    queued?: boolean;
    cartItems?: { name: string; quantity: number; total: number; mhik?: string; unit?: string }[];
    customerName?: string | null;
  } | null;

  const handlePrint = () => {
    const content = receiptRef.current;
    if (!content) return;

    const printWindow = window.open('', '_blank', 'width=300,height=600');
    if (!printWindow) {
      window.print();
      return;
    }

    const itemsHtml = (state?.cartItems || []).map(i =>
      `<div style="display:flex;justify-content:between;font-size:11px;padding:2px 0">
        <span style="flex:1">${i.name} × ${i.quantity} ${i.unit || t('pos_common.unit_default')}</span>
        <span>${new Intl.NumberFormat(intlLocale()).format(i.total)}</span>
      </div>
      ${i.mhik ? `<div style="font-size:9px;color:#888;padding:0 0 2px 0">MHIK: ${i.mhik}</div>` : ''}`
    ).join('');

    const now = new Date();
    const dateStr = now.toLocaleDateString(intlLocale()) + ' ' + now.toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('pos_receipt.title')}</title>
        <style>
          @page { margin: 0; size: 80mm auto; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            width: 80mm;
            padding: 4mm;
            font-size: 12px;
            color: #000;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider {
            border-top: 1px dashed #000;
            margin: 6px 0;
          }
          .row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            font-weight: bold;
            font-size: 14px;
            padding: 4px 0;
          }
          .small { font-size: 10px; color: #666; }
        </style>
      </head>
      <body>
        ${merchant?.logo_url ? `<div class="center" style="margin-bottom:6px"><img src="${merchant.logo_url}" style="max-width:48px;max-height:48px;border-radius:6px;margin:0 auto;display:block" /></div>` : ''}
        <div class="center bold" style="font-size:14px;margin-bottom:4px">${merchant?.name || 'Hisob POS'}</div>
        <div class="center small">${merchant?.address || t('pos_receipt.default_address')}</div>
        <div class="center small">${dateStr}</div>
        ${state?.saleId ? `<div class="center small">${t('pos_receipt.number', { id: state.saleId })}</div>` : ''}
        <div class="divider"></div>
        ${itemsHtml || `<div class="row"><span>${t('pos_receipt.products')}</span><span>${t('pos_common.count_pcs', { n: state?.items ?? 0 })}</span></div>`}
        <div class="divider"></div>
        <div class="row"><span>${t('pos_cart.payment_method')}</span><span>${paymentLabel}</span></div>
        <div class="divider"></div>
        <div class="total-row"><span>${t('pos_receipt.total_caps')}</span><span>${new Intl.NumberFormat(intlLocale()).format(state?.total ?? 0)} so'm</span></div>
        <div class="divider"></div>
        <div class="center small" style="margin-top:8px">${t('pos_receipt.thanks')}</div>
        <script>window.onload=function(){window.print();window.close();}</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const paymentLabel =
    state?.method === 'hisob_wallet' ? t('pos_pay.wallet')
      : state?.method === 'cash' ? t('pos_pay.cash')
      : state?.method === 'card' ? t('pos_pay.card')
      : state?.method === 'murabaha' ? t('pos_pay.murabaha')
      : state?.method || '';

  return (
    <POSLayout>
      <div className="max-w-md mx-auto p-6 text-center animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-1">{t('pos_receipt.success')}</h2>
        {state?.queued ? (
          <p className="text-xs text-warning bg-warning/10 border border-warning/20 rounded-lg px-3 py-2 mb-6">
            {t('pos_receipt.queued')}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground mb-6">{t('pos_receipt.ready')}</p>
        )}

        <div ref={receiptRef} className="bg-card border border-border rounded-xl p-6 mb-6 text-left" id="receipt">
          <div className="text-center mb-4">
            {merchant?.logo_url && (
              <img src={merchant.logo_url} alt={t('pos_receipt.logo_alt')} className="w-12 h-12 rounded-lg object-cover mx-auto mb-2" />
            )}
            <p className="font-bold text-foreground">{merchant?.name || 'Hisob POS'}</p>
            <p className="text-xs text-muted-foreground">{merchant?.address || t('pos_receipt.default_address')}</p>
            {state?.saleId && <p className="text-xs text-muted-foreground mt-1">{t('pos_receipt.number', { id: state.saleId })}</p>}
          </div>

          {state?.cartItems && state.cartItems.length > 0 && (
            <div className="border-t border-dashed border-border py-3 space-y-2">
              {state.cartItems.map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">{item.name} × {item.quantity} {item.unit || t('pos_common.unit_default')}</span>
                    <span className="text-foreground">{formatUZS(item.total)}</span>
                  </div>
                  {item.mhik && (
                    <p className="text-[10px] text-muted-foreground">MHIK: {item.mhik}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-dashed border-border py-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('pos_receipt.products')}</span>
              <span className="text-foreground">{t('pos_common.count_pcs', { n: state?.items ?? 0 })}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('pos_cart.payment_method')}</span>
              <span className="text-foreground capitalize">{paymentLabel}</span>
            </div>
          </div>
          <div className="border-t border-dashed border-border pt-3">
            <div className="flex justify-between">
              <span className="font-semibold text-foreground">{t('pos_cart.total')}</span>
              <span className="font-semibold text-foreground">{formatUZS(state?.total ?? 0)}</span>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">{t('pos_receipt.thanks')}</p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={handlePrint}>
            <Printer size={16} className="mr-2" /> {t('pos_receipt.print')}
          </Button>
          <Button className="flex-1" onClick={() => navigate('/pos')}>
            <ArrowLeft size={16} className="mr-2" /> {t('pos_receipt.new_sale')}
          </Button>
        </div>
      </div>
    </POSLayout>
  );
}
